
import React from 'react';
import { Category } from '@/types/assessment';
import { useQuestionsList } from '@/hooks/useQuestionsList';
import { CategorySelector } from './question-list/CategorySelector';
import { CategoryHeader } from './question-list/CategoryHeader';
import { EmptyState } from './question-list/EmptyStates';
import { QuestionsDragContainer } from './question-list/QuestionsDragContainer';
import { QuestionForm } from './QuestionForm';

interface QuestionsListProps {
  categories: Category[];
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  isMaturityModel?: boolean;
  assessment?: { isMaturityModel?: boolean } | null;
  onContentChange?: () => void;
}

export function QuestionsList({ 
  categories,
  setCategories,
  isMaturityModel = false,
  assessment,
  onContentChange
}: QuestionsListProps) {
  const {
    selectedCategoryId,
    setSelectedCategoryId,
    selectedCategory,
    isAddingQuestion,
    editingQuestionId,
    handleDragEnd,
    handleAddQuestion,
    handleEditQuestion,
    handleCloseQuestionForm,
    handleUpdateQuestion,
    handleUpdateAnswer,
    handleAddAnswer,
    handleSaveQuestion,
    handleDeleteQuestion,
    handleToggleQuestionStatus
  } = useQuestionsList({ 
    categories, 
    setCategories,
    assessment,
    onContentChange
  });

  if (categories.length === 0) {
    return <EmptyState type="categories" onAddQuestion={() => {}} />;
  }

  const editingQuestion = editingQuestionId && selectedCategory 
    ? selectedCategory.questions.find(q => q.id === editingQuestionId) 
    : null;

  return (
    <div className="space-y-6">
      {/* Category selector */}
      <CategorySelector 
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        setSelectedCategoryId={setSelectedCategoryId}
      />

      {selectedCategory ? (
        <div className="space-y-4">
          <CategoryHeader 
            selectedCategory={selectedCategory}
            onAddQuestion={handleAddQuestion}
            isMaturityModel={isMaturityModel}
          />

          {/* Question Form - shown when adding new question or editing */}
          {(isAddingQuestion || editingQuestionId) && (
            <QuestionForm
              categoryId={selectedCategory.id}
              question={editingQuestion}
              onSave={handleSaveQuestion}
              onCancel={handleCloseQuestionForm}
              isMaturityModel={isMaturityModel}
            />
          )}

          {selectedCategory.questions.length === 0 ? (
            <EmptyState type="questions" onAddQuestion={handleAddQuestion} />
          ) : (
            <QuestionsDragContainer 
              selectedCategory={selectedCategory}
              categories={categories}
              setCategories={setCategories}
              onDragEnd={handleDragEnd}
              onUpdateQuestion={handleUpdateQuestion}
              onUpdateAnswer={handleUpdateAnswer}
              onAddAnswer={handleAddAnswer}
              onEditQuestion={handleEditQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onToggleQuestionStatus={handleToggleQuestionStatus}
              isMaturityModel={isMaturityModel}
            />
          )}
        </div>
      ) : (
        <EmptyState type="category" onAddQuestion={() => {}} />
      )}
    </div>
  );
}
